/**
 * CrowdTalkie Core Types
 */

// ============= Location Types =============

export interface GeoLocation {
  latitude: number
  longitude: number
  accuracy?: number
  timestamp?: Date
}

export interface FuzzedLocation {
  latitude: number
  longitude: number
  geohash: string // 6-character precision (~1.2km x 0.6km)
}

// ============= Room Types =============

export interface Room {
  id: string
  name: string
  center: GeoLocation
  radiusMeters: number
  createdAt: Date
  creatorPeerId: string
  secret: string // Base64 encoded room secret for E2E encryption
}

export interface RoomMeta {
  name: string
  center: GeoLocation
  radiusMeters: number
  createdAt: Date
  micMode: MicMode
  chatMode: ChatMode
  poiMode: POIMode
  poiExpirationMinutes: number
  isPaused: boolean
  sharingEnabled: boolean // Can new participants join via QR/link
  allowedTransports?: AllowedTransportsConfig // Which connection types are enabled
}

export type MicMode = 'open' | 'coordinators_only' | 'organizer_only'
export type ChatMode = 'open' | 'slow_mode' | 'coordinators_only'
export type POIMode = 'open' | 'coordinators_only' | 'organizer_only' | 'disabled'

// ============= Identity Types =============

export type Role = 'organizer' | 'coordinator' | 'participant'

export interface RoomIdentity {
  peerId: string
  displayName: string
  publicKey: string // Base64 encoded Ed25519 public key
  role: Role
}

export interface Participant {
  peerId: string
  displayName: string
  role: Role
  joinedAt: Date
  isInsideGeofence: boolean
  lastSeen: Date
  isMuted: boolean
  connectionType?: TransportType // How this participant is connected to the mesh
}

// ============= Message Types =============

export type MessageType = 'text' | 'voice' | 'poi' | 'system'

export interface BaseMessage {
  id: string
  type: MessageType
  senderId: string
  senderName: string
  senderRole: Role
  timestamp: Date
}

export interface TextMessage extends BaseMessage {
  type: 'text'
  content: string
}

export interface VoiceMessage extends BaseMessage {
  type: 'voice'
  audioBlob: Uint8Array
  duration: number // seconds
  transcript?: string
  transcriptConfidence?: number // 0-1
}

export interface POIMessage extends BaseMessage {
  type: 'poi'
  poiType: QuickActionKey
  location: GeoLocation
  address?: string
  expiresAt?: Date
  clearedAt?: Date
  clearedBy?: string
  // Vehicle sighting specific fields (when poiType === 'vehicle_sighting')
  vehicleData?: {
    plate?: string
    plateConfidence?: 'full' | 'partial' | 'none'
    vehicleType?: 'sedan' | 'suv' | 'van' | 'truck' | 'unmarked' | 'other'
    vehicleColor?: string
    direction?: 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW' | 'stationary'
    description?: string
    isFollowing?: boolean
    photos?: Array<{
      id: string
      thumbnailDataUrl: string
      hasLocation: boolean
      plateOCR?: { text: string; confidence: number }
    }>
  }
}

export interface SystemMessage extends BaseMessage {
  type: 'system'
  content: string
  systemType: 'join' | 'leave' | 'role_change' | 'room_setting' | 'poi_cleared'
}

export type Message = TextMessage | VoiceMessage | POIMessage | SystemMessage

// ============= Quick Actions / POI Types =============

export type QuickActionKey =
  | 'need_help'
  | 'blocked_route'
  | 'regroup'
  | 'police'
  | 'medic'
  | 'all_clear'
  | 'vehicle_sighting'

export interface QuickAction {
  key: QuickActionKey
  emoji: string
  label: string
  color: string
  message: string
}

export const QUICK_ACTIONS: Record<QuickActionKey, QuickAction> = {
  vehicle_sighting: {
    key: 'vehicle_sighting',
    emoji: '🚐',
    label: 'Vehicle',
    color: '#92400e',
    message: 'Vehicle sighting reported',
  },
  need_help: {
    key: 'need_help',
    emoji: '⚠️',
    label: 'Need Help',
    color: '#eab308',
    message: 'Requesting assistance at this location',
  },
  blocked_route: {
    key: 'blocked_route',
    emoji: '🚧',
    label: 'Blocked Route',
    color: '#f97316',
    message: 'Route blocked at this location',
  },
  regroup: {
    key: 'regroup',
    emoji: '🧭',
    label: 'Meet Here',
    color: '#22c55e',
    message: 'Meeting point marked',
  },
  police: {
    key: 'police',
    emoji: '🛑',
    label: 'Danger Zone',
    color: '#ef4444',
    message: 'Danger zone reported',
  },
  medic: {
    key: 'medic',
    emoji: '🏥',
    label: 'First Aid',
    color: '#ec4899',
    message: 'First aid available here',
  },
  all_clear: {
    key: 'all_clear',
    emoji: '✅',
    label: 'All Clear',
    color: '#3b82f6',
    message: 'Area is clear',
  },
}

// ============= P2P Connection Types =============

export type ConnectionStatus = 'connected' | 'degraded' | 'disconnected'
export type TransportType = 'webrtc' | 'websocket' | 'bluetooth' | 'lora'

// Allowed transports configuration for room creation
export type AllowedTransport = 'wifi' | 'bluetooth' | 'cellular' | 'lora'

export interface AllowedTransportsConfig {
  wifi: boolean      // WebRTC/WebSocket over WiFi
  bluetooth: boolean // Web Bluetooth mesh fallback
  cellular: boolean  // Mobile data (WebRTC/WebSocket)
  lora: boolean      // LoRA mesh via Meshtastic
}

export const DEFAULT_ALLOWED_TRANSPORTS: AllowedTransportsConfig = {
  wifi: true,
  bluetooth: true,
  cellular: true,
  lora: true,
}

export const TRANSPORT_OPTIONS: Array<{
  key: AllowedTransport
  label: string
  description: string
  icon: string
  range: string
}> = [
  {
    key: 'wifi',
    label: 'WiFi',
    description: 'Direct P2P over local network',
    icon: '🌐',
    range: '~50m',
  },
  {
    key: 'cellular',
    label: 'Cellular',
    description: 'Mobile data relay (requires internet)',
    icon: '📱',
    range: 'Global',
  },
  {
    key: 'bluetooth',
    label: 'Bluetooth',
    description: 'Local mesh when internet fails',
    icon: '📶',
    range: '~100m',
  },
  {
    key: 'lora',
    label: 'LoRA',
    description: 'Long-range mesh via Meshtastic device',
    icon: '📻',
    range: '~10km',
  },
]

export interface P2PStatus {
  status: ConnectionStatus
  activeTransport: TransportType | null
  peerCount: number
  webrtcConnected: boolean
  websocketConnected: boolean
  bluetoothConnected: boolean
  loraConnected: boolean
}

export interface ConnectedPeer {
  peerId: string
  displayName?: string
  transport: TransportType
  lastSeen: Date
}

// ============= Speaking Queue Types =============

export interface SpeakingQueueEntry {
  peerId: string
  displayName: string
  requestedAt: Date
  position: number
}

export interface SpeakingState {
  currentSpeaker: string | null
  queue: SpeakingQueueEntry[]
  isRecording: boolean
  recordingStartedAt?: Date
}

// ============= Speech-to-Text Types =============

export interface SpeechToTextResult {
  transcript: string
  confidence: number
  isFinal: boolean
}

export type TranscriptConfidenceLevel = 'high' | 'medium' | 'low' | 'very_low'

export function getConfidenceLevel(confidence: number): TranscriptConfidenceLevel {
  if (confidence > 0.9) return 'high'
  if (confidence > 0.7) return 'medium'
  if (confidence > 0.5) return 'low'
  return 'very_low'
}

export function getConfidenceIndicator(confidence: number): string {
  const level = getConfidenceLevel(confidence)
  switch (level) {
    case 'high': return '✓✓'
    case 'medium': return '✓'
    case 'low': return '~'
    case 'very_low': return '?'
  }
}

// ============= Geofence Types =============

export interface GeofenceStatus {
  isInside: boolean
  distanceToCenter: number
  distanceBeyondRadius?: number
  /** Whether this status was calculated using LoRa extended boundary */
  isLoRaExtended?: boolean
  /** The effective radius used (may be extended for LoRa) */
  effectiveRadius?: number
}

export const GEOFENCE_RADIUS_OPTIONS = [50, 100, 150, 250, 500] as const
export type GeofenceRadius = typeof GEOFENCE_RADIUS_OPTIONS[number]

// ============= LoRa Extended Boundary Constants =============

/**
 * Extended boundary radius for LoRa devices (10km)
 * LoRa mesh can reach 2-15km, so we allow connections from
 * much further than the standard room geofence (max 500m).
 */
export const LORA_EXTENDED_BOUNDARY_METERS = 10000

/**
 * Minimum room radius threshold to activate LoRa extended boundary.
 * Rooms smaller than 50m probably don't want long-range connections.
 * This is the threshold for enabling the LoRa extended boundary feature,
 * not a general room size constraint.
 */
export const LORA_EXTENDED_BOUNDARY_ACTIVATION_THRESHOLD = 50

// ============= Room Access Request Types =============

export type AccessRequestStatus = 'pending' | 'approved' | 'denied' | 'expired'

export interface AccessRequest {
  id: string
  roomId: string
  requesterPeerId: string
  requesterName: string
  requestedAt: Date
  status: AccessRequestStatus
  respondedAt?: Date
  respondedBy?: string
}

export interface RoomSecuritySettings {
  allowAccessRequests: boolean // Can participants request access from organizer
  autoApproveInGeofence: boolean // Auto-approve requests from users inside geofence
  securityDelegate?: string // PeerId of member who can handle access requests
}

// ============= Incident Command System (ICS) Hierarchy Types =============

/**
 * Hierarchy roles in the ICS-style command structure
 * - commander: Top-level authority in the command room
 * - deputy: Commander's backup, can act on behalf of commander
 * - sector_lead: Leads a sector room (equivalent to organizer in a sector)
 * - team_member: Regular participant in any room
 */
export type HierarchyRole = 'commander' | 'deputy' | 'sector_lead' | 'team_member'

/**
 * Room type in the hierarchy
 * - command: Top-level command room with overview of all sectors
 * - sector: A sector room under command (e.g., "North Street Team")
 * - standalone: Regular room not part of any hierarchy
 */
export type HierarchyRoomType = 'command' | 'sector' | 'standalone'

/**
 * Hierarchy structure for a room
 */
export interface RoomHierarchy {
  type: HierarchyRoomType
  parentRoomId?: string // For sector rooms - the command room ID
  childRoomIds: string[] // For command rooms - list of sector room IDs
  commanderPublicKey?: string // For command rooms - the commander's signing key
  createdAt: Date
}

/**
 * Extended room interface with hierarchy support
 */
export interface HierarchyRoom extends Room {
  hierarchy: RoomHierarchy
}

/**
 * Sector information for command room dashboard
 */
export interface SectorInfo {
  roomId: string
  name: string
  participantCount: number
  location: GeoLocation
  leadPeerId?: string
  leadName?: string
  status: 'active' | 'offline' | 'alert'
  lastActivity: Date
  pendingEscalations: number
}

/**
 * Cross-room identity for hierarchy participants
 * Allows same identity across hierarchy while maintaining per-room unlinkability to outsiders
 */
export interface CrossRoomIdentity {
  masterPublicKey: string // Base64 encoded master Ed25519 public key
  encryptedMasterPrivateKey: string // Encrypted with storage key
  hierarchyId: string // The command room ID this identity belongs to
  roomBindings: RoomBinding[]
}

export interface RoomBinding {
  roomId: string
  derivedPeerId: string
  derivedPublicKey: string
  role: HierarchyRole
  assignedAt: Date
}

// ============= ICS Message Types =============

/**
 * Priority levels for escalated reports
 */
export type EscalationPriority = 'routine' | 'urgent' | 'critical'

/**
 * Escalated report from sector to command
 */
export interface EscalatedReport {
  type: 'escalated_report'
  id: string
  sourceRoomId: string
  sourceRoomName: string
  originalMessage: Message
  escalatedBy: string // Sector lead peerId
  escalatedByName: string
  priority: EscalationPriority
  timestamp: Date
  signature: string // Ed25519 signature by sector lead
  acknowledged?: boolean
  acknowledgedBy?: string
  acknowledgedAt?: Date
}

/**
 * Command broadcast from command room to sectors
 */
export interface CommandBroadcast {
  type: 'command_broadcast'
  id: string
  commandRoomId: string
  targetRoomIds: string[] | 'all'
  message: TextMessage | VoiceMessage
  broadcastBy: string // Commander or deputy peerId
  broadcastByName: string
  priority: EscalationPriority
  timestamp: Date
  signature: string // Ed25519 signature by commander/deputy
}

/**
 * Resource assignment - moving participants between sectors
 */
export interface ResourceAssignment {
  type: 'resource_assignment'
  id: string
  fromRoomId: string
  toRoomId: string
  participantPeerIds: string[] // People being reassigned
  assignedBy: string // Commander peerId
  assignedByName: string
  reason?: string
  timestamp: Date
  signature: string // Ed25519 signature by commander
}

/**
 * Hierarchy structure change (add/remove sector)
 */
export interface HierarchyChange {
  type: 'hierarchy_change'
  id: string
  action: 'add_sector' | 'remove_sector' | 'update_lead'
  commandRoomId: string
  sectorRoomId: string
  sectorName?: string
  sectorLeadPeerId?: string
  changedBy: string // Commander peerId
  timestamp: Date
  signature: string
}

// ============= Hierarchy Proof for Verification =============

/**
 * Cryptographic proof that a sector belongs to a command room
 * Used to verify cross-room messages
 */
export interface HierarchyProof {
  commandRoomId: string
  sectorRoomId: string
  sectorName: string
  sectorCreationTimestamp: Date
  commanderPublicKey: string
  signature: string // Commander signed the sector creation
}

// ============= ICS Dashboard Types =============

/**
 * Aggregated hierarchy status for commander dashboard
 */
export interface HierarchyStatus {
  commandRoomId: string
  sectors: SectorInfo[]
  totalParticipants: number
  activeAlerts: number
  pendingEscalations: EscalatedReport[]
  recentBroadcasts: CommandBroadcast[]
  lastUpdated: Date
}

/**
 * Sector status indicators
 */
export type SectorStatusType = 'active' | 'offline' | 'alert' | 'moving'

export interface SectorStatusUpdate {
  sectorRoomId: string
  status: SectorStatusType
  participantCount: number
  location?: GeoLocation
  timestamp: Date
}

// ============= ICS GossipSub Topics =============

/**
 * Topic patterns for hierarchy communication
 */
export const HIERARCHY_TOPICS = {
  /** Reports escalated from sectors to command */
  escalations: (commandRoomId: string) => `/crowdtalkie/hierarchy/${commandRoomId}/escalations`,
  /** Broadcasts from command to sectors */
  broadcasts: (commandRoomId: string) => `/crowdtalkie/hierarchy/${commandRoomId}/broadcasts`,
  /** Resource movement assignments */
  assignments: (commandRoomId: string) => `/crowdtalkie/hierarchy/${commandRoomId}/assignments`,
  /** Hierarchy structure changes */
  structure: (commandRoomId: string) => `/crowdtalkie/hierarchy/${commandRoomId}/structure`,
  /** Sector status updates (heartbeats) */
  sectorStatus: (commandRoomId: string) => `/crowdtalkie/hierarchy/${commandRoomId}/status`,
} as const

// ============= ICS Constants =============

/** Maximum number of sectors per command room */
export const MAX_SECTORS_PER_COMMAND = 8

/** Maximum number of deputies per command room */
export const MAX_DEPUTIES = 3

/** Default sector status heartbeat interval in ms */
export const SECTOR_HEARTBEAT_INTERVAL_MS = 30000

/** Sector considered offline after this many missed heartbeats */
export const SECTOR_OFFLINE_THRESHOLD_HEARTBEATS = 3

/** Maximum pending escalations before warning */
export const MAX_PENDING_ESCALATIONS_WARNING = 10

// ============= ICS Utility Functions =============

/**
 * Get display color for escalation priority
 */
export function getEscalationPriorityColor(priority: EscalationPriority): string {
  switch (priority) {
    case 'critical': return '#ef4444' // Red
    case 'urgent': return '#f97316' // Orange
    case 'routine': return '#22c55e' // Green
  }
}

/**
 * Get display icon for escalation priority
 */
export function getEscalationPriorityIcon(priority: EscalationPriority): string {
  switch (priority) {
    case 'critical': return '🔴'
    case 'urgent': return '🟡'
    case 'routine': return '🟢'
  }
}

/**
 * Get display color for sector status
 */
export function getSectorStatusColor(status: SectorStatusType): string {
  switch (status) {
    case 'active': return '#22c55e' // Green
    case 'alert': return '#f97316' // Orange
    case 'moving': return '#3b82f6' // Blue
    case 'offline': return '#6b7280' // Gray
  }
}

/**
 * Check if a role can escalate reports
 */
export function canEscalate(role: HierarchyRole): boolean {
  return role === 'sector_lead' || role === 'commander' || role === 'deputy'
}

/**
 * Check if a role can broadcast commands
 */
export function canBroadcast(role: HierarchyRole): boolean {
  return role === 'commander' || role === 'deputy'
}

/**
 * Check if a role can reassign participants
 */
export function canReassign(role: HierarchyRole): boolean {
  return role === 'commander'
}

/**
 * Check if a role can modify hierarchy structure
 */
export function canModifyHierarchy(role: HierarchyRole): boolean {
  return role === 'commander'
}
